/**
 * Wire contract for `GET /reviews/product/:productId/insight` (SkinSeoulFacade, dev).
 *
 * Mirrors `apps/api/src/model/ReviewInsight.ts` on the backend. The response is a stable wrapper:
 * `reviewInsight` is `null` whenever no publishable insight exists — the backend collapses every
 * unavailable state (processing, not_mapped, …) into `null` and does NOT send an `availability`
 * field. Domain shapes live in `model/whatPeopleSay/whatPeopleSay.ts`; do not add them here.
 */

/** One localized topic counted over unique product reviews. */
export interface ReviewInsightMention {
  id: string;
  label: string;
  /** Reviews mentioning this topic. */
  count: number;
  /** Localized frequency wording chosen by the server, e.g. 'Often noted'. */
  mentionFrequency: string;
  /** Single representative review excerpt (unquoted). */
  representativeExcerpt: string;
}

/**
 * Topic groups by polarity.
 *
 * With NEITHER polarity available the backend sends `reviewInsight: null`, which the converter
 * maps to the "gathering" card — so an empty `mentions` block never means "both groups empty".
 *
 * A SINGLE empty group renders that group's empty-state card (Figma `1081:16444`). The deployed
 * backend still documents 3–5 items per group (its "five-versus-three readiness policy"), so that
 * payload cannot arrive yet; relaxing that `minItems` is a backend change still to come. Nothing
 * here changes when it lands — an empty array already flows straight through.
 */
export interface ReviewInsightMentions {
  positive: ReviewInsightMention[];
  caution: ReviewInsightMention[];
}

/** Per-group review-grounded summary sentences. */
export interface ReviewInsightSummary {
  positiveText: string;
  cautionText: string;
}

/** A ready insight. `averageRating` is omitted when no valid rating exists. */
export interface ProductReviewInsight {
  reviewCount: number;
  averageRating?: number;
  summary: ReviewInsightSummary;
  mentions: ReviewInsightMentions;
  updatedAt: string;
}

export interface GetProductReviewInsightRequest {
  /** Catalog product ID (positive integer) */
  productId: number;
  /**
   * The user's language, forwarded as-is — the server decides what copy to serve for one it has
   * no translation for. Omitted → the backend serves `en`.
   */
  locale?: string;
}

/** Stable response wrapper — `reviewInsight` is `null` when no publishable insight exists. */
export interface GetProductReviewInsightResponse {
  reviewInsight: ProductReviewInsight | null;
}
